/**
 * Appends the shop name configured in admin (`app.seo.shop_name`, surfaced by the root
 * loader). Without one the title stands on its own rather than trailing a separator.
 */
export const buildTitle = (title: string, shopName?: string) => (shopName ? `${title} | ${shopName}` : title);

/**
 * The homepage SEO block, mapped from the prefixed field names the settings group uses
 * onto the shape every other page type already speaks. Read from the root match, which
 * fetches that group once for the shop name anyway.
 */
export const getHomepageSeo = (matches: { id: string; data?: unknown }[]): ApiSeo => {
  const settings =
    (matches.find((match) => match.id === 'root')?.data as { seoSettings?: Record<string, string | null> } | undefined)
      ?.seoSettings ?? {};

  return {
    metaTitle: settings.homepage_meta_title,
    metaDescription: settings.homepage_meta_description,
    ogTitle: settings.homepage_og_title,
    ogDescription: settings.homepage_og_description,
    ogImage: settings.homepage_og_image,
    ogImageWidth: toNumber(settings.homepage_og_image_width),
    ogImageHeight: toNumber(settings.homepage_og_image_height),
  };
};

// Settings values arrive JSON-decoded; numbers are numbers, but the record is typed as
// strings for the text fields, so coerce defensively.
const toNumber = (value: string | null | undefined): number | null => (value ? Number(value) : null);

/** The shop name from the root loader, for meta functions that only get `matches`. */
export const getShopName = (matches: { id: string; data?: unknown }[]): string =>
  (matches.find((match) => match.id === 'root')?.data as { shopName?: string } | undefined)?.shopName ?? '';

/**
 * Facebook's crawler cannot fetch images from the API host (Cloudflare in front of it breaks
 * the crawler's fetch, while the same bytes from this host render fine), so Open Graph
 * renditions are re-pointed at the `/og-media/*` proxy route on the shop origin. Anything
 * that is not an OG rendition is left untouched.
 */
export const toSharingImageUrl = (imageUrl: string, origin: string): string => {
  const match = imageUrl.match(/\/media\/cache\/(app_og_image|app_og_product_image)\/(.+)$/);
  if (!match || !origin) return imageUrl;
  return `${origin}/og-media/${match[1]}/${match[2]}`;
};

/** Sharing image used by any page that does not carry one of its own. */
export const SITE_OG_IMAGE_PATH = '/og-image.png';
export const SITE_OG_IMAGE_WIDTH = 938;
export const SITE_OG_IMAGE_HEIGHT = 563;
export const SITE_OG_IMAGE_TYPE = 'image/png';

/**
 * `og:image` plus the size/type tags scrapers use to render a share card immediately —
 * without them Facebook fetches the image asynchronously and the first share of a URL
 * can go out without a picture.
 */
export const ogImageMeta = (
  url: string,
  width: number | null | undefined,
  height: number | null | undefined,
  type: string
) => {
  const meta: { property: string; content: string }[] = [{ property: 'og:image', content: url }];
  if (width && height) {
    meta.push({ property: 'og:image:width', content: String(width) });
    meta.push({ property: 'og:image:height', content: String(height) });
  }
  meta.push({ property: 'og:image:type', content: type });
  return meta;
};

/**
 * The shop's own origin, read from the root loader. Open Graph needs absolute URLs, and
 * a meta function has no request of its own — the root match is where the origin lives.
 */
export const getRootOrigin = (matches: { id: string; data?: unknown }[]): string =>
  (matches.find((match) => match.id === 'root')?.data as { origin?: string } | undefined)?.origin ?? '';

/**
 * The SEO block the API serves alongside a product or a taxon. Every field is optional
 * because the backend fills them in gradually — taxons currently send nothing but nulls,
 * and no product has a `metaDescription` yet.
 */
export interface ApiSeo {
  metaTitle?: string | null;
  metaDescription?: string | null;
  metaKeywords?: string | null;
  ogTitle?: string | null;
  ogDescription?: string | null;
  ogImage?: string | null;
  /** Pixel size of `ogImage`; the API sends 1200x630 for every image it serves. */
  ogImageWidth?: number | null;
  ogImageHeight?: number | null;
}

interface BuildSeoMetaArgs {
  seo: ApiSeo | undefined;
  /** Used for the title when the API has no `metaTitle` — a name, typically. */
  fallbackTitle: string;
  /** Used for the description when the API has no `metaDescription`. */
  fallbackDescription?: string;
  /** Shop origin, so the site-wide sharing image can be given as an absolute URL. */
  origin?: string;
  /** Shop name from admin, appended to the title unless the API already did it. */
  shopName?: string;
  /** Request pathname, so `og:url` can be emitted as the canonical absolute URL. */
  path?: string;
  /** Open Graph object type; products say `product`, everything else `website`. */
  ogType?: 'website' | 'product';
}

/**
 * Turns the API's SEO fields into Remix meta descriptors, shared by every page type so
 * the tags stay identical wherever they are rendered.
 *
 * Values come straight from the API — computing them is the backend's job. The two
 * fallbacks exist only because the backend does not fill those two fields yet; the
 * moment it does, its values win. The shop name is the one thing the frontend owns.
 *
 * `og:image` falls back to the site-wide image, because a page's own image has to be
 * able to replace it — Remix hands the leaf route's meta to the document in place of the
 * parent's, so a default declared upstream would simply vanish here. The remaining tags
 * are left out entirely when there is nothing to say, rather than shipped blank.
 */
export const buildSeoMeta = ({
  seo,
  fallbackTitle,
  fallbackDescription,
  origin,
  shopName,
  path,
  ogType,
}: BuildSeoMetaArgs) => {
  const title = seo?.metaTitle || fallbackTitle;
  const description = seo?.metaDescription || fallbackDescription || '';

  // A title from the API is used verbatim — the backend already ends it with the shop
  // name, and appending it here as well is how it landed on the page twice. Only the
  // fallback title is the frontend's own, so only that one gets the shop name added.
  const documentTitle = seo?.metaTitle || buildTitle(fallbackTitle, shopName);

  const meta: Array<Record<string, string>> = [{ title: documentTitle }];

  if (description) {
    meta.push({ name: 'description', content: description });
  }

  if (seo?.metaKeywords) {
    meta.push({ name: 'keywords', content: seo.metaKeywords });
  }

  // og:type / og:url are "required" by Facebook's debugger; without them every scrape
  // carries a warning even when the preview renders fine.
  meta.push({ property: 'og:type', content: ogType ?? 'website' });
  if (origin && path !== undefined) {
    meta.push({ property: 'og:url', content: `${origin}${path.split('?')[0]}` });
  }
  meta.push({ property: 'og:title', content: seo?.ogTitle || title });

  if (shopName) {
    // Where the shop name belongs when sharing, rather than trailing `og:title` the way
    // it trails the document title. Pages with no SEO record in the API — the cart, the
    // account screens — would otherwise share under a bare "Můj účet".
    meta.push({ property: 'og:site_name', content: shopName });
  }

  const ogDescription = seo?.ogDescription || description;
  if (ogDescription) {
    meta.push({ property: 'og:description', content: ogDescription });
  }

  // An empty `og:image` reads to a scraper as a broken image rather than as no image
  // at all, so the tags are only worth emitting once there is something to point at.
  if (seo?.ogImage) {
    // The API always serves a JPEG rendition, whatever the upload was.
    meta.push(
      ...ogImageMeta(toSharingImageUrl(seo.ogImage, origin ?? ''), seo.ogImageWidth, seo.ogImageHeight, 'image/jpeg')
    );
  } else if (origin) {
    meta.push(
      ...ogImageMeta(`${origin}${SITE_OG_IMAGE_PATH}`, SITE_OG_IMAGE_WIDTH, SITE_OG_IMAGE_HEIGHT, SITE_OG_IMAGE_TYPE)
    );
  }

  return meta;
};

/**
 * Meta for a page whose title the frontend owns — the cart, the account screens and the
 * like, which have no SEO block in the API. They still get the shop-wide sharing image:
 * a route's meta replaces the root's rather than extending it, so without this they
 * would render no `og:image` at all.
 */
export const buildPageMeta = (title: string, matches: { id: string; data?: unknown }[]) =>
  buildSeoMeta({
    seo: undefined,
    fallbackTitle: title,
    origin: getRootOrigin(matches),
    shopName: getShopName(matches),
  });
